'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Profile {
  id: string;
  username: string;
  role: 'teacher' | 'super_admin';
  is_default: boolean;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<Profile | null>;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Demo session helper
function getDemoSession(): { email: string; role: string; username: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('demo_admin_session');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sessions expire after 8 hours
    if (Date.now() - parsed.loginTime > 8 * 60 * 60 * 1000) {
      sessionStorage.removeItem('demo_admin_session');
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) {
        setProfile(data as Profile);
      }
    } catch {
      // Profile fetch failed silently
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: supaSession } }) => {
      if (supaSession?.user) {
        setSession(supaSession);
        setUser(supaSession.user);
        fetchProfile(supaSession.user.id);
        setLoading(false);
        return;
      }

      // Check for demo session fallback
      const demoSession = getDemoSession();
      if (demoSession) {
        const demoUser = {
          id: demoSession.role === 'super_admin' ? 'demo-super-admin' : 'demo-teacher',
          email: demoSession.email,
          role: 'authenticated',
          isDemoUser: true,
        };
        const demoProfile: Profile = {
          id: demoUser.id,
          username: demoSession.username,
          role: demoSession.role as 'teacher' | 'super_admin',
          is_default: demoSession.role === 'super_admin',
          created_at: new Date().toISOString(),
        };
        setUser(demoUser);
        setProfile(demoProfile);
      }
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, supaSession) => {
      if (supaSession?.user) {
        setSession(supaSession);
        setUser(supaSession.user);
        fetchProfile(supaSession.user.id);
      } else {
        // Check demo session on sign-out event
        const demoSession = getDemoSession();
        if (demoSession) {
          const demoUser = {
            id: demoSession.role === 'super_admin' ? 'demo-super-admin' : 'demo-teacher',
            email: demoSession.email,
            role: 'authenticated',
            isDemoUser: true,
          };
          const demoProfile: Profile = {
            id: demoUser.id,
            username: demoSession.username,
            role: demoSession.role as 'teacher' | 'super_admin',
            is_default: demoSession.role === 'super_admin',
            created_at: new Date().toISOString(),
          };
          setUser(demoUser);
          setProfile(demoProfile);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
        }
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    // Clear demo session
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('demo_admin_session');
    }
    const { error } = await supabase.auth.signOut();
    if (error && !error.message.includes('session')) throw error;
    setProfile(null);
    setUser(null);
    setSession(null);
  };

  const getCurrentUser = async () => {
    const { data: { user: supaUser }, error } = await supabase.auth.getUser();
    if (error) return user; // Return local user if supabase fails
    return supaUser || user;
  };

  const isEmailVerified = () => {
    if (user?.isDemoUser) return true;
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<Profile | null> => {
    if (!user) return null;
    if (user?.isDemoUser) return profile;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data as Profile | null;
  };

  const isAdmin = () => {
    return profile?.role === 'teacher' || profile?.role === 'super_admin';
  };

  const isSuperAdmin = () => {
    return profile?.role === 'super_admin';
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
